/**
 * キャンセルモーダル（Task 14）。
 *
 * 予約を「テンプレ別の文面 + 内部メモ」でキャンセルするための UI。
 *
 * UI:
 * - テンプレ選択（3択ラジオ）：お客様都合 / 店舗都合 / その他
 *   ※ 予約変更（再予約済み）は Phase 1.5.4 で顧客側 LIFF に移管したため admin 側からは廃止
 * - 各テンプレの「お客様にこう届きます」プレビュー（選択中のテンプレを表示）
 * - 内部メモ（textarea・最大500字・任意）
 * - 「実行内容」明示（D1 / Calendar 削除 / LINE通知 / タグ付与）
 * - 確定 → POST /api/admin/reservations/:id/cancel
 *
 * Worker 側の results に false が含まれていたら warning トーストで知らせる。
 */

import { cancelReservation } from "../api";
import { showToast } from "../render";
import type { CancelTemplateKey, Reservation } from "../types";

interface TemplateMeta {
  key: CancelTemplateKey;
  label: string;
  buildPreview: (r: Reservation) => string;
}

/**
 * Worker 側 src/lib/cancelTemplates.ts の文面と一致させる。
 * 二重管理のリスクは承知の上で、毎回 API で取得する遅延を避けるためフロント側にも持つ。
 * Worker 側を変更したらこの定数も更新すること。
 */
const TEMPLATES: TemplateMeta[] = [
  {
    key: "customer",
    label: "お客様都合",
    buildPreview: (r) => [
      `${r.customer_name} さま`,
      "",
      `${formatMd(r.reservation_date)} ${r.reservation_time} のご予約をキャンセル承りました。`,
      "",
      "ご連絡ありがとうございます。",
      "ちょっとでも気になることがあれば、お気軽にご相談ください😊",
      "",
      "ご予約はコチラから▾",
      "https://liff.line.me/2009825101-1s2yD67F",
      "",
      "Empath LINE",
    ].join("\n"),
  },
  {
    key: "shop",
    label: "店舗都合",
    buildPreview: (r) => [
      `${r.customer_name} さま`,
      "",
      `${formatMd(r.reservation_date)} ${r.reservation_time} のご予約につきまして、`,
      "こちらの予定が合わず誠に勝手ながら、キャンセルとさせていただきます。",
      "ご迷惑をおかけし申し訳ございません🙇",
      "",
      "別の日程でのご相談を承りますので、",
      "ご都合のよろしい日時をこのトークでお知らせください。",
      "",
      "Empath LINE",
    ].join("\n"),
  },
  {
    key: "other",
    label: "その他",
    buildPreview: (r) => [
      `${r.customer_name} さま`,
      "",
      `${formatMd(r.reservation_date)} ${r.reservation_time} のご予約をキャンセル承りました。`,
      "",
      "ご質問・ご要望がございましたら、",
      "このトークでお気軽にお知らせください。",
      "",
      "Empath LINE",
    ].join("\n"),
  },
];

/**
 * キャンセルモーダルを開く。確定 → 成功時に onDone() を呼ぶ（通常は詳細を再描画）。
 */
export function openCancelModal(r: Reservation, onDone: () => void): void {
  // 既存モーダルがあれば閉じる
  closeModal();

  let selectedKey: CancelTemplateKey = "customer";

  const wrap = document.createElement("div");
  wrap.className = "modal-backdrop";
  wrap.id = "modal-cancel";
  wrap.innerHTML = renderHtml(r, selectedKey);
  document.body.appendChild(wrap);

  // 背景タップで閉じる（モーダル本体のタップは伝播させない）
  wrap.addEventListener("click", (ev) => {
    if (ev.target === wrap) closeModal();
  });

  const updatePreview = () => {
    const previewEl = document.getElementById("cancel-preview");
    if (!previewEl) return;
    const tpl = TEMPLATES.find((t) => t.key === selectedKey)!;
    previewEl.textContent = tpl.buildPreview(r);
  };

  // ラジオ変更でプレビュー更新
  wrap.querySelectorAll<HTMLInputElement>('input[name="tpl"]').forEach((el) => {
    el.addEventListener("change", () => {
      if (el.checked) {
        selectedKey = el.value as CancelTemplateKey;
        updatePreview();
      }
    });
  });

  // 閉じるボタン
  document.getElementById("cancel-close")?.addEventListener("click", () => closeModal());
  document.getElementById("cancel-back")?.addEventListener("click", () => closeModal());

  // 確定
  document.getElementById("cancel-submit")?.addEventListener("click", async () => {
    const noteEl = document.getElementById("cancel-note") as HTMLTextAreaElement | null;
    const note = (noteEl?.value ?? "").slice(0, 500);

    const submitBtn = document.getElementById("cancel-submit") as HTMLButtonElement | null;
    if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = "実行中..."; }

    try {
      const result = await cancelReservation(r.id, selectedKey, note);

      // results を見て partial failure をユーザーに知らせる
      const fails: string[] = [];
      if (r.google_calendar_event_id && !result.results.calendar_deleted) fails.push("カレンダー削除");
      if (!result.results.customer_pushed) fails.push("お客様への通知");
      // tag_attached は Harness 未連携の友だちもありえるので警告対象から外す（false でも正常ケース）

      closeModal();
      if (fails.length > 0) {
        showToast(`キャンセル完了。ただし ${fails.join(" / ")} に失敗しました`, "warning", 6000);
      } else {
        showToast("キャンセルしました", "success");
      }
      onDone();
    } catch (e: any) {
      showToast(`キャンセル失敗：${e?.message ?? e}`, "error", 5000);
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = "実行する"; }
    }
  });

  // 初期プレビュー
  updatePreview();
}

function renderHtml(r: Reservation, initialKey: CancelTemplateKey): string {
  return `
    <div class="modal-card" role="dialog" aria-modal="true" aria-labelledby="cancel-title">
      <div class="flex items-start justify-between mb-3">
        <h2 id="cancel-title" class="text-lg font-bold text-gray-900">予約をキャンセル</h2>
        <button
          id="cancel-close"
          aria-label="閉じる"
          class="tap-target text-gray-500 hover:text-gray-700 px-2">✕</button>
      </div>

      <div class="text-sm text-gray-700 mb-3">
        <span class="font-semibold">${esc(r.customer_name)}</span> さま<br>
        ${esc(formatYmd(r.reservation_date))} ${esc(r.reservation_time)}
      </div>

      <fieldset class="mb-4">
        <legend class="text-sm font-semibold text-gray-800 mb-2">キャンセル理由（お客様に届く文面が変わります）</legend>
        ${TEMPLATES.map((t) => `
          <label class="flex items-start gap-2 p-2 border border-gray-200 rounded mb-1.5 cursor-pointer">
            <input
              type="radio"
              name="tpl"
              value="${t.key}"
              ${t.key === initialKey ? "checked" : ""}
              class="mt-1">
            <span class="text-sm text-gray-800">${esc(t.label)}</span>
          </label>
        `).join("")}
      </fieldset>

      <div class="mb-4">
        <div class="text-xs text-gray-500 mb-1">お客様にこの文面が届きます</div>
        <pre id="cancel-preview"
          class="text-xs bg-gray-50 border border-gray-200 rounded p-2 whitespace-pre-wrap text-gray-800 leading-relaxed"></pre>
      </div>

      <div class="mb-4">
        <label for="cancel-note" class="block text-sm font-semibold text-gray-800 mb-1">
          内部メモ（任意・500字まで）
        </label>
        <p class="text-xs text-gray-500 mb-1">
          社内記録のみ。お客様には届きません。
        </p>
        <textarea
          id="cancel-note"
          maxlength="500"
          rows="3"
          class="w-full p-2 border border-gray-300 rounded text-sm"
          placeholder="例：お客様から体調不良で延期希望。来週再連絡予定。"></textarea>
      </div>

      <div class="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4 text-xs text-gray-700 leading-relaxed">
        <div class="font-semibold mb-1">実行内容</div>
        <ul class="list-disc pl-4 space-y-0.5">
          <li>D1 のステータスを「キャンセル」に更新</li>
          <li>Google カレンダーの予定を削除</li>
          <li>お客様の LINE に上記の文面を送信</li>
          <li>LINE Harness に「キャンセル済み」タグを付与</li>
        </ul>
      </div>

      <div class="flex gap-2">
        <button
          id="cancel-back"
          class="tap-target flex-1 px-4 py-3 bg-gray-200 text-gray-800 rounded-lg font-semibold">
          戻る
        </button>
        <button
          id="cancel-submit"
          class="tap-target flex-1 px-4 py-3 bg-red-600 text-white rounded-lg font-semibold">
          実行する
        </button>
      </div>
    </div>
  `;
}

function closeModal(): void {
  document.getElementById("modal-cancel")?.remove();
}

function esc(s: string | null | undefined): string {
  if (s === null || s === undefined) return "";
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function formatMd(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${Number(m[2])}/${Number(m[3])}`;
}

function formatYmd(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  return `${m[1]}/${Number(m[2])}/${Number(m[3])}`;
}
